"use client";

import React, { useEffect, useMemo } from "react";
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import i18nIsoCountries from "i18n-iso-countries";
import it from "i18n-iso-countries/langs/it.json";
import Logo from "@/assets/icons/Logo";
import LogoReduced from "@/assets/icons/LogoReduced";
import { Providers } from "@/libs/shared/providers/Providers";
import i18n from "@/libs/shared/i18n/init";
import MessageHandler from "@/libs/shared/messages/MessageHandler";
import useEnvVariables from "@/libs/shared/hooks/useEnvVariables";
import AppInitializer from "@/libs/shared/components/AppInitializer";
import type { Analytics } from "firebase/analytics";
import type { ReactNode } from "react";

export default function App({ children }: Readonly<{ children: ReactNode }>) {
  const env = useEnvVariables();

  const analytics: Analytics | undefined = useMemo(() => {
    if (typeof window === "undefined" || !env) return;

    const app = initializeApp({
      apiKey: env?.FIREBASE_API_KEY,
      authDomain: env?.FIREBASE_AUTH_DOMAIN,
      projectId: env?.FIREBASE_PROJECT_ID,
      storageBucket: env?.FIREBASE_STORAGE_BUCKET,
      messagingSenderId: env?.FIREBASE_MESSAGING_SENDER_ID,
      appId: env?.FIREBASE_APP_ID,
      measurementId: env?.FIREBASE_MEASUREMENT_ID,
    });

    return getAnalytics(app);
  }, [env]);

  useEffect(() => {
    i18nIsoCountries.registerLocale(it);
  }, []);

  return (
    <Providers
      i18n={i18n}
      staticImages={{ loginBg: "/login.webp", workImage: "/work.png" }}
      Logo={Logo}
      LogoReduced={LogoReduced}
      analytics={analytics}
    >
      <MessageHandler />
      <AppInitializer>{children}</AppInitializer>
    </Providers>
  );
}
